import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Truck, Plus, Edit2, Trash2, Loader2, IndianRupee, Wallet, MapPin, Activity, Printer } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const TripDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Inline forms state
  const today = new Date().toISOString().split('T')[0];
  const [expForm, setExpForm] = useState({ date: today, expense_type: '', amount: '', note: '' });
  const [payForm, setPayForm] = useState({ date: today, amount: '', note: '' });
  const [logForm, setLogForm] = useState({ start_location: '', start_date: today, start_km: '', end_location: '', end_date: '', end_km: '' });
  const [statusForm, setStatusForm] = useState({ status: '', charge: '', docket_numbers: '', receiver_name: '', date: today, rate_type: '', loading_unloading_charge: '', location: '', current_km: '' });
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingLog, setEditingLog] = useState(null);

  const rateTypes = [
    { value: 'minus_temp_rate', label: 'Minus Temp Rate' },
    { value: 'plus_temp_rate', label: 'Plus Temp Rate' },
    { value: 'general_rate', label: 'General Rate' },
    { value: 'cross_border_rate', label: 'Cross Border Rate' },
    { value: 'local_temp', label: 'Local Temp' },
    { value: 'local_gen', label: 'Local General' },
  ];

  const rateTypeLabel = (rt) => {
    const found = rateTypes.find(r => r.value === rt);
    return found ? found.label : (rt || '—');
  };

  const expenseTypes = ['URIYA (AD BLUE)', 'DIESEL', 'TOLL', 'POLICE', 'PUNCTURE', 'OIL', 'GREASE', 'MISCELLANEOUS', 'LOADING', 'UNLOADING', 'MECHANICAL', 'WARAI', 'PARKING', 'UNION', 'ENTRY', 'GREEN TAX', 'GURKHA TAX', 'HOLD CHARGES'];
  const statusOptions = ['PARKING', 'LOADING', 'IN_TRANSIT', 'UNLOADING', 'COMPLETED', 'CANCELLED'];

  const fetchDetail = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/detail`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      setDetail(await res.json());
    } catch {
      toast({ title: "Error", description: "Failed to load trip details", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getAuthHeaders, id, toast]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Pre-fill rate_type from trip for first status log
  useEffect(() => {
    if (detail?.trip?.rate_type && !statusForm.rate_type) {
      setStatusForm(p => ({ ...p, rate_type: detail.trip.rate_type }));
    }
  }, [detail]);

  // ===== EXPENSES =====
  const addExpense = async () => {
    if (!expForm.expense_type || !expForm.amount) return;
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/expenses`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ ...expForm, amount: parseFloat(expForm.amount) || 0 }),
      });
      if (!res.ok) throw new Error();
      setExpForm({ date: today, expense_type: '', amount: '', note: '' });
      fetchDetail();
    } catch { toast({ title: "Error", description: "Failed to add expense", variant: "destructive" }); }
  };

  const updateExpense = async () => {
    if (!editingExpense) return;
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/expenses/${editingExpense.id}`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({
          date: editingExpense.date,
          expense_type: editingExpense.expense_type,
          amount: parseFloat(editingExpense.amount) || 0,
          note: editingExpense.note,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(JSON.stringify(err));
      }
      setEditingExpense(null);
      fetchDetail();
    } catch (error) { 
      toast({ title: "Error", description: error.message || "Failed to update expense", variant: "destructive" }); 
    }
  };

  // ===== PAYMENTS =====
  const addPayment = async () => {
    if (!payForm.amount) return;
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/payments`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ ...payForm, amount: parseFloat(payForm.amount) || 0 }),
      });
      if (!res.ok) throw new Error();
      setPayForm({ date: today, amount: '', note: '' });
      fetchDetail();
    } catch { toast({ title: "Error", description: "Failed to add payment", variant: "destructive" }); }
  };

  const updatePayment = async () => {
    if (!editingPayment) return;
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/payments/${editingPayment.id}`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({
          date: editingPayment.date,
          amount: parseFloat(editingPayment.amount) || 0,
          note: editingPayment.note,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(JSON.stringify(err));
      }
      setEditingPayment(null);
      fetchDetail();
    } catch (error) { 
      toast({ title: "Error", description: error.message || "Failed to update payment", variant: "destructive" }); 
    }
  };

  // ===== LOGS =====
  const addLog = async () => {
    if (!logForm.start_location) return;
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/logs`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          ...logForm,
          start_km: parseFloat(logForm.start_km) || 0,
          end_km: parseFloat(logForm.end_km) || 0,
        }),
      });
      if (!res.ok) throw new Error();
      setLogForm({ start_location: '', start_date: today, start_km: '', end_location: '', end_date: '', end_km: '' });
      fetchDetail();
    } catch { toast({ title: "Error", description: "Failed to add log", variant: "destructive" }); }
  };

  const updateLog = async () => {
    if (!editingLog) return;
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/logs/${editingLog.id}`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({
          start_location: editingLog.start_location,
          start_date: editingLog.start_date,
          start_km: parseFloat(editingLog.start_km) || 0,
          end_location: editingLog.end_location,
          end_date: editingLog.end_date || null,
          end_km: parseFloat(editingLog.end_km) || 0,
        }),
      });
      if (!res.ok) throw new Error();
      setEditingLog(null);
      fetchDetail();
    } catch { toast({ title: "Error", description: "Failed to update log", variant: "destructive" }); }
  };


  // ===== STATUS =====
  const addStatusLog = async () => {
    if (!statusForm.status) return;
    const isLU = ['LOADING', 'UNLOADING'].includes(statusForm.status);
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/status-logs`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          ...statusForm,
          charge: parseFloat(statusForm.charge) || 0,
          rate_type: statusForm.rate_type || null,
          loading_unloading_charge: isLU ? (parseFloat(statusForm.loading_unloading_charge) || 0) : 0,
          location: statusForm.location || null,
          current_km: statusForm.current_km ? parseFloat(statusForm.current_km) : null,
        }),
      });
      if (!res.ok) throw new Error();
      const msg = statusForm.location
        ? `Status updated. Trip log auto-updated with location "${statusForm.location}".`
        : 'Status updated successfully.';
      setStatusForm(p => ({ status: '', charge: '', docket_numbers: '', receiver_name: '', date: today, rate_type: p.rate_type, loading_unloading_charge: '', location: '', current_km: '' }));
      if (isLU && parseFloat(statusForm.loading_unloading_charge) > 0) {
        toast({ title: "Status Updated", description: `${statusForm.status} charge auto-added to expenses` });
      } else {
        toast({ title: "Status Updated", description: msg });
      }
      fetchDetail();
    } catch { toast({ title: "Error", description: "Failed to update status", variant: "destructive" }); }
  };

  // ===== AUTO BATTHU =====
  const addAutoBatthu = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/trips/${id}/auto-batthu`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast({ title: "Batthu Added", description: data.message });
      fetchDetail();
    } catch (err) { toast({ title: "Error", description: err.message, variant: "destructive" }); }
  };

  // ===== DELETE =====
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { type, itemId } = deleteTarget;
    const urlMap = {
      expense: `${backendUrl}/api/trips/${id}/expenses/${itemId}`,
      payment: `${backendUrl}/api/trips/${id}/payments/${itemId}`,
      log: `${backendUrl}/api/trips/${id}/logs/${itemId}`,
      status_log: `${backendUrl}/api/trips/${id}/status-logs/${itemId}`,
    };
    try {
      const res = await fetch(urlMap[type], { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      fetchDetail();
    } catch { toast({ title: "Error", description: "Failed to delete", variant: "destructive" }); }
    setDeleteTarget(null);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-accent-blue" /></div>;
  if (!detail) return <div className="text-center py-20 text-text-secondary font-ibm">Trip not found</div>;

  const { trip } = detail;
  const tripType = (trip.trip_type || 'local').charAt(0).toUpperCase() + (trip.trip_type || 'local').slice(1);

  const isLUState = ['LOADING', 'UNLOADING'].includes(statusForm.status);
  let availableDockets = [];
  if (isLUState && trip?.dockets) {
    const usedDockets = new Set();
    detail.status_logs.forEach(log => {
      if (log.status === statusForm.status && log.docket_numbers) {
        log.docket_numbers.split(',').map(d => d.trim()).forEach(d => usedDockets.add(d));
      }
    });
    availableDockets = trip.dockets.map(d => d.system_docket_no).filter(dno => !usedDockets.has(dno));
  }

  const handleDocketToggle = (dno) => {
    const current = statusForm.docket_numbers ? statusForm.docket_numbers.split(',').map(d => d.trim()).filter(Boolean) : [];
    if (current.includes(dno)) {
      setStatusForm(p => ({...p, docket_numbers: current.filter(d => d !== dno).join(', ')}));
    } else {
      setStatusForm(p => ({...p, docket_numbers: [...current, dno].join(', ')}));
    }
  };

  const statusBadgeClass = (s) => {
    const map = { ACTIVE: 'bg-green-100 text-green-700', COMPLETED: 'bg-blue-100 text-blue-700', CANCELLED: 'bg-red-100 text-red-700' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/fleet/trips')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 data-testid="trip-detail-title" className="text-2xl font-bold text-text-dark font-manrope">Trip Detail</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate(`/fleet/trips/print/${id}`)} data-testid="print-trip-btn"
            className="bg-green-600 hover:bg-green-700 text-white font-ibm font-semibold h-10 px-5 rounded-lg">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button onClick={() => navigate(`/fleet/trips/edit/${id}`)} data-testid="edit-trip-btn"
            className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-10 px-6 rounded-lg">
            <Edit2 className="w-4 h-4 mr-2" /> Edit Trip
          </Button>
        </div>
      </div>

      {/* Trip Identity Bar */}
      <Card className="bg-primary-blue text-white rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-3 text-sm font-ibm">
          <span className="font-bold text-lg font-manrope">{trip.trip_number}</span>
          <span className="opacity-60">|</span>
          <span>DRIVER: <strong>{trip.driver_name || '—'}</strong></span>
          <span className="opacity-60">|</span>
          <span>VEHICLE: <strong>{trip.vehicle_registration}</strong></span>
          <span className="opacity-60">|</span>
          <span>TYPE: <strong>{tripType}</strong></span>
          <span className="opacity-60">|</span>
          <Badge className={`${statusBadgeClass(trip.status)} border-0 font-ibm`}>{trip.status}</Badge>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: 'Opening Balance', value: (trip.opening_balance || 0).toLocaleString(), icon: Wallet, color: 'text-blue-600' },
          { label: 'Net Balance', value: detail.net_balance.toLocaleString(), icon: IndianRupee, color: detail.net_balance >= 0 ? 'text-green-600' : 'text-red-600' },
          { label: 'Actual Expense', value: detail.total_expense.toLocaleString(), icon: IndianRupee, color: 'text-orange-600' },
          { label: 'Total Batthu', value: detail.total_batthu.toLocaleString(), icon: Activity, color: 'text-purple-600' },
          { label: 'Total Travel', value: `${detail.total_travel_km.toLocaleString()} KM`, icon: MapPin, color: 'text-teal-600' },
        ].map((c, i) => (
          <Card key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <c.icon className={`w-4 h-4 ${c.color}`} />
              <span className="text-xs text-text-secondary font-ibm">{c.label}</span>
            </div>
            <p data-testid={`summary-${c.label.toLowerCase().replace(/\s/g, '-')}`} className={`text-xl font-bold font-manrope ${c.color}`}>{c.value}</p>
          </Card>
        ))}
      </div>

      {/* Driver Allocation + Driver Ledger */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h3 className="text-base font-semibold text-text-dark font-manrope mb-3">Driver(s) Allocated to Trip</h3>
        <table className="w-full text-sm font-ibm">
          <thead className="bg-gray-50">
            <tr>
              {['#', 'Name', 'Role', 'Batthu Rate', 'Status'].map(h => (
                <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-text-secondary uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="px-3 py-2">1</td>
              <td className="px-3 py-2 font-medium">{trip.driver_name || '—'}</td>
              <td className="px-3 py-2">Driver</td>
              <td className="px-3 py-2">{(trip.trip_type || 'local') === 'local' ? 'Local' : 'Outstation'}</td>
              <td className="px-3 py-2"><Badge className="bg-green-100 text-green-700 border-0 text-xs">Assigned</Badge></td>
            </tr>
            {trip.conductor_name && (
              <tr>
                <td className="px-3 py-2">2</td>
                <td className="px-3 py-2 font-medium">{trip.conductor_name}</td>
                <td className="px-3 py-2">Conductor</td>
                <td className="px-3 py-2">Conductor Batthu</td>
                <td className="px-3 py-2"><Badge className="bg-green-100 text-green-700 border-0 text-xs">Assigned</Badge></td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

        {/* Driver Ledger Summary Moved Here */}
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
          <table className="w-full text-sm font-ibm h-full">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-bold text-text-dark uppercase">Final Figure</th>
                <th className="px-4 py-2.5 text-right text-xs font-bold text-text-dark uppercase w-32">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-semibold text-text-secondary uppercase">Total Expense</div>
                  <div className="text-[10px] text-gray-400 font-normal mt-0.5">Freight + Loading + Unloading + Urea + Batthu + Receipts</div>
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-text-dark">{(detail.totals?.H_total_expense || 0).toLocaleString()}</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-semibold text-text-secondary uppercase">Total Receipts</div>
                  <div className="text-[10px] text-gray-400 font-normal mt-0.5">Diesel + Advance/NEFT Payments</div>
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-accent-blue">{(detail.totals?.K_total_receipts || 0).toLocaleString()}</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase">Balance</td>
                <td className="px-4 py-2.5 text-right font-bold">
                  {(() => {
                    const bal = (detail.totals?.K_total_receipts || 0) - (detail.totals?.H_total_expense || 0);
                    return (
                      <div className={`flex flex-col items-end ${bal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <span>{bal > 0 ? '+' : ''}{bal.toLocaleString()}</span>
                        <span className="text-[10px] font-normal opacity-80">({bal >= 0 ? 'Debit' : 'Credit'})</span>
                      </div>
                    );
                  })()}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase">Opening Balance (Previous Dues)</td>
                <td className="px-4 py-2.5 text-right font-bold text-orange-600">{(trip.opening_balance || 0).toLocaleString()}</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-4 py-3 text-xs font-bold text-text-dark uppercase">Final Balance</td>
                <td className="px-4 py-3 text-right font-bold">
                  {(() => {
                    const net = detail.net_balance || 0;
                    return (
                      <div className={`flex flex-col items-end ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <span>{net > 0 ? '+' : ''}{net.toLocaleString()}</span>
                        <span className="text-[10px] font-normal opacity-80">({net >= 0 ? 'Debit' : 'Credit'})</span>
                      </div>
                    );
                  })()}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      {/* Trip Expenses + Payment Logs side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Trip Expenses */}
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-text-dark font-manrope">Trip Expenses</h3>
            <Button size="sm" variant="outline" onClick={addAutoBatthu} data-testid="auto-batthu-btn"
              className="text-xs font-ibm h-8 border-accent-blue text-accent-blue hover:bg-accent-blue/5">
              <Plus className="w-3 h-3 mr-1" /> Auto Batthu
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-ibm" data-testid="expenses-table">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Date', 'Expense', 'Amount', 'Note', 'Action'].map(h => (
                    <th key={h} className="px-2 py-2 text-left text-xs font-semibold text-text-secondary uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* New row */}
                <tr className="bg-blue-50/50">
                  <td className="px-2 py-1.5 text-xs text-accent-blue font-semibold">New</td>
                  <td className="px-2 py-1.5"><Input type="date" value={expForm.date} onChange={e => setExpForm(p => ({...p, date: e.target.value}))} className="h-8 text-xs w-28" /></td>
                  <td className="px-2 py-1.5">
                    <Select value={expForm.expense_type || '_pick_'} onValueChange={v => setExpForm(p => ({...p, expense_type: v === '_pick_' ? '' : v}))}>
                      <SelectTrigger className="h-8 text-xs w-24"><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>{expenseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5"><Input type="number" value={expForm.amount} onChange={e => setExpForm(p => ({...p, amount: e.target.value}))} placeholder="0" className="h-8 text-xs w-20" /></td>
                  <td className="px-2 py-1.5"><Input value={expForm.note} onChange={e => setExpForm(p => ({...p, note: e.target.value}))} placeholder="Note" className="h-8 text-xs w-28" /></td>
                  <td className="px-2 py-1.5">
                    <button onClick={addExpense} data-testid="add-expense-btn" className="w-7 h-7 flex items-center justify-center bg-accent-blue text-white rounded-md hover:bg-accent-blue/90">
                      <Plus className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                {/* Existing */}
                {detail.expenses.map((e, i) => (
                  <tr key={e.id}>
                    {editingExpense?.id === e.id ? (
                      <>
                        <td className="px-2 py-1.5 text-xs">{i + 1}</td>
                        <td className="px-2 py-1.5"><Input type="date" value={editingExpense.date} onChange={ev => setEditingExpense(p => ({...p, date: ev.target.value}))} className="h-8 text-xs w-28" /></td>
                        <td className="px-2 py-1.5">
                          <Select value={editingExpense.expense_type} onValueChange={v => setEditingExpense(p => ({...p, expense_type: v}))}>
                            <SelectTrigger className="h-8 text-xs w-24"><SelectValue /></SelectTrigger>
                            <SelectContent>{expenseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5"><Input type="number" value={editingExpense.amount} onChange={ev => setEditingExpense(p => ({...p, amount: ev.target.value}))} className="h-8 text-xs w-20" /></td>
                        <td className="px-2 py-1.5"><Input value={editingExpense.note || ''} onChange={ev => setEditingExpense(p => ({...p, note: ev.target.value}))} className="h-8 text-xs w-28" /></td>
                        <td className="px-2 py-1.5 flex gap-1">
                          <button onClick={updateExpense} className="text-green-600 hover:bg-green-50 rounded p-1 text-xs font-semibold">Save</button>
                          <button onClick={() => setEditingExpense(null)} className="text-gray-500 hover:bg-gray-50 rounded p-1 text-xs">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-1.5 text-xs text-text-secondary">{i + 1}</td>
                        <td className="px-2 py-1.5 text-xs whitespace-nowrap">{e.date}</td>
                        <td className="px-2 py-1.5"><Badge className="bg-gray-100 text-gray-700 border-0 text-xs">{e.expense_type}</Badge></td>
                        <td className="px-2 py-1.5 text-xs font-medium">{(e.amount || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-xs text-text-secondary truncate max-w-[100px]" title={e.note}>{e.note || '—'}</td>
                        <td className="px-2 py-1.5 flex gap-1">
                          <button onClick={() => setEditingExpense({...e})} className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"><Edit2 className="w-3.5 h-3.5 text-accent-blue" /></button>
                          <button onClick={() => setDeleteTarget({ type: 'expense', itemId: e.id })} className="w-6 h-6 flex items-center justify-center hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {detail.expenses.length === 0 && (
                  <tr><td colSpan={6} className="px-2 py-4 text-center text-xs text-text-secondary">No expenses yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Payment Logs */}
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-base font-semibold text-text-dark font-manrope mb-3">Payment Logs</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-ibm" data-testid="payments-table">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Date', 'Amount', 'Note', 'Action'].map(h => (
                    <th key={h} className="px-2 py-2 text-left text-xs font-semibold text-text-secondary uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="bg-blue-50/50">
                  <td className="px-2 py-1.5 text-xs text-accent-blue font-semibold">New</td>
                  <td className="px-2 py-1.5"><Input type="date" value={payForm.date} onChange={e => setPayForm(p => ({...p, date: e.target.value}))} className="h-8 text-xs w-28" /></td>
                  <td className="px-2 py-1.5"><Input type="number" value={payForm.amount} onChange={e => setPayForm(p => ({...p, amount: e.target.value}))} placeholder="0" className="h-8 text-xs w-24" /></td>
                  <td className="px-2 py-1.5"><Input value={payForm.note} onChange={e => setPayForm(p => ({...p, note: e.target.value}))} placeholder="Note" className="h-8 text-xs w-32" /></td>
                  <td className="px-2 py-1.5">
                    <button onClick={addPayment} data-testid="add-payment-btn" className="w-7 h-7 flex items-center justify-center bg-accent-blue text-white rounded-md hover:bg-accent-blue/90">
                      <Plus className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                {detail.payments.map((p, i) => (
                  <tr key={p.id}>
                    {editingPayment?.id === p.id ? (
                      <>
                        <td className="px-2 py-1.5 text-xs">{i + 1}</td>
                        <td className="px-2 py-1.5"><Input type="date" value={editingPayment.date} onChange={ev => setEditingPayment(pr => ({...pr, date: ev.target.value}))} className="h-8 text-xs w-28" /></td>
                        <td className="px-2 py-1.5"><Input type="number" value={editingPayment.amount} onChange={ev => setEditingPayment(pr => ({...pr, amount: ev.target.value}))} className="h-8 text-xs w-24" /></td>
                        <td className="px-2 py-1.5"><Input value={editingPayment.note || ''} onChange={ev => setEditingPayment(pr => ({...pr, note: ev.target.value}))} className="h-8 text-xs w-32" /></td>
                        <td className="px-2 py-1.5 flex gap-1">
                          <button onClick={updatePayment} className="text-green-600 hover:bg-green-50 rounded p-1 text-xs font-semibold">Save</button>
                          <button onClick={() => setEditingPayment(null)} className="text-gray-500 hover:bg-gray-50 rounded p-1 text-xs">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-1.5 text-xs text-text-secondary">{i + 1}</td>
                        <td className="px-2 py-1.5 text-xs whitespace-nowrap">{p.date}</td>
                        <td className="px-2 py-1.5 text-xs font-medium">{(p.amount || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-xs text-text-secondary">{p.note || '—'}</td>
                        <td className="px-2 py-1.5 flex gap-1">
                          <button onClick={() => setEditingPayment({...p})} className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"><Edit2 className="w-3.5 h-3.5 text-accent-blue" /></button>
                          <button onClick={() => setDeleteTarget({ type: 'payment', itemId: p.id })} className="w-6 h-6 flex items-center justify-center hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {detail.payments.length === 0 && (
                  <tr><td colSpan={5} className="px-2 py-4 text-center text-xs text-text-secondary">No payments yet</td></tr>
                )}
              </tbody>
            </table>
          </div>


        </Card>
      </div>

      {/* Trip Logs */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h3 className="text-base font-semibold text-text-dark font-manrope mb-3">Trip Logs</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-ibm" data-testid="trip-logs-table">
            <thead className="bg-gray-50">
              <tr>
                {['#', 'Start Location', 'Start Date', 'Start KM', 'End Location', 'End Date', 'End KM', 'Distance', 'Rate (₹)', 'Amount', 'Action'].map(h => (
                  <th key={h} className="px-2 py-2 text-left text-xs font-semibold text-text-secondary uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {detail.logs.map((l, i) => {
                const dist = l.distance ?? Math.max(0, (l.end_km || 0) - (l.start_km || 0));
                const logRate = l.rate_amount ?? (trip.rate_amount || 0);
                const amount = logRate * dist;
                return (
                  <tr key={l.id}>
                    {editingLog?.id === l.id ? (
                      <>
                        <td className="px-2 py-1.5 text-xs">{i + 1}</td>
                        <td className="px-2 py-1.5"><Input value={editingLog.start_location || ''} onChange={e => setEditingLog(p => ({...p, start_location: e.target.value}))} className="h-8 text-xs w-24" /></td>
                        <td className="px-2 py-1.5"><Input type="date" value={editingLog.start_date || ''} onChange={e => setEditingLog(p => ({...p, start_date: e.target.value}))} className="h-8 text-xs w-28" /></td>
                        <td className="px-2 py-1.5"><Input type="number" value={editingLog.start_km} onChange={e => setEditingLog(p => ({...p, start_km: e.target.value}))} className="h-8 text-xs w-20" /></td>
                        <td className="px-2 py-1.5"><Input value={editingLog.end_location || ''} onChange={e => setEditingLog(p => ({...p, end_location: e.target.value}))} className="h-8 text-xs w-24" /></td>
                        <td className="px-2 py-1.5"><Input type="date" value={editingLog.end_date || ''} onChange={e => setEditingLog(p => ({...p, end_date: e.target.value}))} className="h-8 text-xs w-28" /></td>
                        <td className="px-2 py-1.5"><Input type="number" value={editingLog.end_km} onChange={e => setEditingLog(p => ({...p, end_km: e.target.value}))} className="h-8 text-xs w-20" /></td>
                        <td className="px-2 py-1.5 text-xs text-text-secondary">—</td>
                        <td className="px-2 py-1.5 text-xs font-semibold text-accent-blue">{logRate ? `₹${logRate}` : '—'}</td>
                        <td className="px-2 py-1.5 text-xs text-green-700">—</td>
                        <td className="px-2 py-1.5 flex gap-1">
                          <button onClick={updateLog} className="text-green-600 hover:bg-green-50 rounded p-1 text-xs font-semibold">Save</button>
                          <button onClick={() => setEditingLog(null)} className="text-gray-500 hover:bg-gray-50 rounded p-1 text-xs">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-1.5 text-xs text-text-secondary">{i + 1}</td>
                        <td className="px-2 py-1.5 text-xs font-medium">{l.start_location || '—'}</td>
                        <td className="px-2 py-1.5 text-xs whitespace-nowrap">{l.start_date || '—'}</td>
                        <td className="px-2 py-1.5 text-xs">{(l.start_km || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-xs font-medium">{l.end_location || '—'}</td>
                        <td className="px-2 py-1.5 text-xs whitespace-nowrap">{l.end_date || '—'}</td>
                        <td className="px-2 py-1.5 text-xs">{(l.end_km || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-xs font-semibold">{dist > 0 ? `${dist.toLocaleString()} km` : '—'}</td>
                        <td className="px-2 py-1.5 text-xs font-semibold text-accent-blue">{logRate ? `₹${logRate}` : '—'}</td>
                        <td className="px-2 py-1.5 text-xs font-semibold text-green-700">{amount > 0 ? `₹${amount.toLocaleString()}` : '—'}</td>
                        <td className="px-2 py-1.5 flex gap-1">
                          <button onClick={() => setEditingLog({...l})} className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"><Edit2 className="w-3.5 h-3.5 text-accent-blue" /></button>
                          <button onClick={() => setDeleteTarget({ type: 'log', itemId: l.id })} className="w-6 h-6 flex items-center justify-center hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              {detail.logs.length === 0 && (
                <tr><td colSpan={11} className="px-2 py-4 text-center text-xs text-text-secondary">No trip logs yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Update Trip Status */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h3 className="text-base font-semibold text-text-dark font-manrope mb-3">Update Trip Status</h3>
        <div className="flex items-center gap-2 mb-4 text-sm font-ibm text-text-secondary">
          <span>Vehicle: <strong className="text-text-dark">{trip.vehicle_registration}</strong></span>
          <span className="opacity-40">|</span>
          <span>Trip: <strong className="text-text-dark">{trip.trip_number}</strong></span>
          <span className="opacity-40">|</span>
          <span>Current Status: <Badge className={`${statusBadgeClass(trip.status)} border-0 text-xs`}>{trip.status}</Badge></span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
          <div>
            <p className="text-xs text-text-secondary mb-1 font-ibm">Date</p>
            <Input type="date" value={statusForm.date} onChange={e => setStatusForm(p => ({...p, date: e.target.value}))} className="h-9 text-sm" />
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-1 font-ibm">Next Status</p>
            <Select value={statusForm.status || '_pick_'} onValueChange={v => setStatusForm(p => ({...p, status: v === '_pick_' ? '' : v}))}>
              <SelectTrigger data-testid="status-select" className="h-9 text-sm"><SelectValue placeholder="Next Status" /></SelectTrigger>
              <SelectContent>{statusOptions.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-1 font-ibm">Rate Type</p>
            <Select value={statusForm.rate_type || '_none_'} onValueChange={v => setStatusForm(p => ({...p, rate_type: v === '_none_' ? '' : v}))}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Rate Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">None</SelectItem>
                {rateTypes.map(rt => <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-1 font-ibm">Location</p>
            <Input placeholder="Current location..." value={statusForm.location} onChange={e => setStatusForm(p => ({...p, location: e.target.value}))} className="h-9 text-sm" />
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-1 font-ibm">Odometer (KM)</p>
            <Input type="number" min="0" placeholder="Current KM" value={statusForm.current_km} onChange={e => setStatusForm(p => ({...p, current_km: e.target.value}))} className="h-9 text-sm" />
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-1 font-ibm">Charge</p>
            <Input type="number" placeholder="Charge" value={statusForm.charge} onChange={e => setStatusForm(p => ({...p, charge: e.target.value}))} className="h-9 text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
          <div>
            <p className="text-xs text-text-secondary mb-1 font-ibm">Receiver Name</p>
            <Input placeholder="Receiver Name" value={statusForm.receiver_name} onChange={e => setStatusForm(p => ({...p, receiver_name: e.target.value}))} className="h-9 text-sm" />
          </div>
          <div className="flex items-end">
            <Button onClick={addStatusLog} data-testid="update-status-btn" className="bg-accent-blue hover:bg-accent-blue/90 text-white h-9 w-full text-sm font-ibm">
              Update Status
            </Button>
          </div>
        </div>

        {/* Loading / Unloading charge — shown only when that status is selected */}
        {['LOADING', 'UNLOADING'].includes(statusForm.status) && (
          <div className="flex items-center gap-3 mt-1 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex-1">
              <p className="text-xs font-semibold text-amber-800 font-ibm mb-1">
                {statusForm.status === 'LOADING' ? 'Loading Charge' : 'Unloading Charge'}
                <span className="ml-2 font-normal text-amber-600">→ auto-added to Trip Expenses</span>
              </p>
              <Input
                type="number"
                min="0"
                placeholder={`${statusForm.status === 'LOADING' ? 'Loading' : 'Unloading'} charge amount`}
                value={statusForm.loading_unloading_charge}
                onChange={e => setStatusForm(p => ({...p, loading_unloading_charge: e.target.value}))}
                className="h-9 text-sm max-w-xs bg-white"
              />
            </div>
          </div>
        )}

        {/* Docket Numbers Checkboxes for L/U */}
        {isLUState && trip.dockets && trip.dockets.length > 0 && (
          <div className="mb-4 p-3 border border-gray-100 rounded-lg bg-gray-50">
            <p className="text-xs font-semibold text-text-dark font-ibm mb-2">Existing Docket(s)</p>
            {availableDockets.length > 0 ? (
              <div className="flex flex-wrap gap-4 mb-2">
                {availableDockets.map(dno => {
                  const isChecked = (statusForm.docket_numbers || '').includes(dno);
                  return (
                    <label key={dno} className="flex items-center gap-2 text-sm font-ibm cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={isChecked}
                        onChange={() => handleDocketToggle(dno)}
                        className="rounded border-gray-300 text-accent-blue focus:ring-accent-blue"
                      />
                      {dno}
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-text-secondary italic mb-2">All dockets have been {statusForm.status.toLowerCase()}.</p>
            )}
            
            {statusForm.docket_numbers && (
              <p className="text-xs text-text-secondary mt-2">
                Selected: <span className="font-medium text-text-dark">{statusForm.docket_numbers}</span>
              </p>
            )}
          </div>
        )}

        {/* Current Docket Numbers Fallback */}
        {!isLUState && trip.dockets && trip.dockets.length > 0 && (
          <p className="text-xs text-text-secondary font-ibm mb-3">
            Current Docket Numbers: {trip.dockets.map(d => d.system_docket_no).join(', ')}
          </p>
        )}

        {/* Status Logs Table */}
        {detail.status_logs.length > 0 && (
          <div className="overflow-x-auto mt-3">
            <h4 className="text-sm font-semibold text-text-dark font-manrope mb-2">Trip Status Logs</h4>
            <table className="w-full text-sm font-ibm" data-testid="status-logs-table">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Status', 'Date', 'Location', 'KM', 'Rate Type', 'Charge', 'L/U Charge', 'Receiver', 'Dockets', 'Action'].map(h => (
                    <th key={h} className="px-2 py-2 text-left text-xs font-semibold text-text-secondary uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detail.status_logs.map((sl, i) => (
                  <tr key={sl.id}>
                    <td className="px-2 py-1.5 text-xs">{i + 1}</td>
                    <td className="px-2 py-1.5"><Badge className="bg-gray-100 text-gray-700 border-0 text-xs">{sl.status.replace('_', ' ')}</Badge></td>
                    <td className="px-2 py-1.5 text-xs whitespace-nowrap">{sl.date || '—'}</td>
                    <td className="px-2 py-1.5 text-xs font-medium">{sl.location || '—'}</td>
                    <td className="px-2 py-1.5 text-xs font-mono">{sl.current_km != null ? sl.current_km.toLocaleString() : '—'}</td>
                    <td className="px-2 py-1.5 text-xs text-accent-blue">{rateTypeLabel(sl.rate_type)}</td>
                    <td className="px-2 py-1.5 text-xs">{sl.charge || '—'}</td>
                    <td className="px-2 py-1.5 text-xs">
                      {sl.loading_unloading_charge > 0
                        ? <span className="text-amber-700 font-medium">{sl.loading_unloading_charge}</span>
                        : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-xs">{sl.receiver_name || '—'}</td>
                    <td className="px-2 py-1.5 text-xs text-text-secondary max-w-[120px] truncate" title={sl.docket_numbers}>{sl.docket_numbers || '-'}</td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => setDeleteTarget({ type: 'status_log', itemId: sl.id })} className="w-6 h-6 flex items-center justify-center hover:bg-red-50 rounded">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this {deleteTarget?.type?.replace('_', ' ')}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TripDetail;

